import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

interface ContrastModeContextValue {
  highContrast: boolean
  toggle: () => void
}

const ContrastModeContext = createContext<ContrastModeContextValue | null>(null)

const STORAGE_KEY = "rf-scholarships:high-contrast"

/**
 * Real high-contrast theme toggle, replacing the legacy's blunt
 * `!important` black-text override (migration plan §7). Sets
 * `data-contrast="true"` on the root element so `src/styles/globals.css`
 * can swap in a WCAG-AA-checked color scheme via CSS variables.
 */
export function ContrastModeProvider({ children }: { children: ReactNode }) {
  const [highContrast, setHighContrast] = useState(() => {
    if (typeof window === "undefined") return false
    return window.localStorage.getItem(STORAGE_KEY) === "true"
  })

  useEffect(() => {
    document.documentElement.setAttribute("data-contrast", String(highContrast))
    window.localStorage.setItem(STORAGE_KEY, String(highContrast))
  }, [highContrast])

  const value = useMemo(
    () => ({ highContrast, toggle: () => setHighContrast((v) => !v) }),
    [highContrast],
  )

  return <ContrastModeContext.Provider value={value}>{children}</ContrastModeContext.Provider>
}

export function useContrastMode() {
  const ctx = useContext(ContrastModeContext)
  if (!ctx) throw new Error("useContrastMode must be used within ContrastModeProvider")
  return ctx
}
